import { ChangeEventHandler, FC, Fragment, KeyboardEventHandler, useMemo, useReducer } from 'react';

import { FieldArray, FormikProvider, getIn, useFormik } from 'formik';
import { Plus, Wallpaper, X } from 'lucide-react';
import { isNonNullish, isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { DateTimePicker } from '@mui/x-date-pickers';
import { PickerValue } from '@mui/x-date-pickers/internals';

import { LOTTERY_PRIZE_STUB, LotteryPrizeForm, LotterySettingsForm, validationSchema } from './form';
import { mapDtoToForm, mapFormToDto } from './utils';

import css from './UISettingsButton.module.css';

import { casinoService } from 'v2/api/casinoService';
import { lotteriesService } from 'v2/api/lotteriesService';
import { FileSelector } from 'v2/components/fileSystem/fileSelector/FileSelector';
import { Loader } from 'v2/components/loader/Loader';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';
import { Select } from 'v2/components/select/Select';
import { Switch } from 'v2/components/switch/Switch';
import { LotteryType } from 'v2/constants';
import { SelectOption } from 'v2/types';
import { enumToSelect, handleHttpError, preventNonDigitKeyDown } from 'v2/utils';

const PRIZE_TEXT_HELPER = 'Use &nbsp; to keep related text together. Example: 10&nbsp;000&nbsp;₽.';

export const UISettingsButton: FC = () => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data, isLoading, mutate } = useSWR('getLotterySettings', lotteriesService.getLotterySettings);
  const { data: casinos } = useSWR(isOpen && 'getCasinos', casinoService.getCasinos);

  const initialFormValues = useMemo(() => mapDtoToForm(data), [data]);
  const typeOptions = useMemo(() => enumToSelect(LotteryType), []);

  const casinoOptions: SelectOption[] = useMemo(() => {
    if (isNullish(casinos)) return [];

    return casinos.map((casino) => ({ id: casino.id, value: casino.id, label: casino.name }));
  }, [casinos]);

  const { trigger, isMutating } = useSWRMutation('updateLotterySettings', lotteriesService.updateLotterySettings, {
    onSuccess: () => {
      toast.success('Lottery settings have been updated');
      mutate();
      setOpen();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<LotterySettingsForm>({
    initialValues: initialFormValues,
    onSubmit: (values) => {
      trigger(mapFormToDto(values));
    },
    validationSchema,
    enableReinitialize: true,
  });

  const handleSelectChange = (key: keyof LotterySettingsForm) => (v: string) => {
    formik.setFieldValue(key, v);
  };

  const handleChangeDate = (field: keyof LotterySettingsForm) => (value: PickerValue) => {
    formik.setFieldValue(field, value);
  };

  const handleClose = () => {
    setOpen();
    formik.resetForm();
  };

  const endDateError =
    formik.touched.endDate && typeof formik.errors.endDate === 'string' ? formik.errors.endDate : undefined;

  const handlePrizePlacesCountChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    formik.setFieldValue('prizePlacesCount', e.target.value.replace(/[^\d]/g, ''));
  };

  const handlePrizePlacesCountKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    preventNonDigitKeyDown(e);
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<Wallpaper />} variant="contained" size="large">
        UI Settings
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
        <DialogTitle>UI Settings</DialogTitle>

        {isLoading ? (
          <Loader />
        ) : (
          isNonNullish(data) && (
            <Fragment>
              <DialogContent>
                <FormikProvider value={formik}>
                  <form id="lotterySettingsForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
                    <Select
                      value={formik.values.type}
                      options={typeOptions}
                      label="Type"
                      onChange={handleSelectChange('type')}
                      required
                    />

                    <RichTextarea
                      label="Short Description"
                      value={formik.values.shortDescription}
                      onChange={handleSelectChange('shortDescription')}
                      required
                    />

                    <RichTextarea
                      label="Full Description"
                      value={formik.values.fullDescription}
                      onChange={handleSelectChange('fullDescription')}
                      required
                    />

                    <Select
                      value={formik.values.casinoId}
                      options={casinoOptions}
                      onChange={handleSelectChange('casinoId')}
                      label="Casino"
                      required
                    />

                    <div className="form-group">
                      <FileSelector
                        value={formik.values.desktopImage}
                        onSelect={handleSelectChange('desktopImage')}
                        label="Desktop Image"
                      />

                      <FileSelector
                        value={formik.values.tabletImage}
                        onSelect={handleSelectChange('tabletImage')}
                        label="Tablet Image"
                      />

                      <FileSelector
                        value={formik.values.mobileImage}
                        onSelect={handleSelectChange('mobileImage')}
                        label="Mobile Image"
                      />
                    </div>

                    <div className={css.datesGroup}>
                      <DateTimePicker
                        name="startDate"
                        value={formik.values.startDate}
                        onChange={handleChangeDate('startDate')}
                        label="Start Date"
                        format="DD.MM.YYYY HH:mm"
                      />

                      <DateTimePicker
                        name="endDate"
                        value={formik.values.endDate}
                        onChange={handleChangeDate('endDate')}
                        label="End Date"
                        format="DD.MM.YYYY HH:mm"
                        slotProps={{
                          textField: {
                            required: true,
                            error: Boolean(endDateError),
                            helperText: endDateError,
                          },
                        }}
                      />
                    </div>

                    <TextField
                      name="prizePlacesCount"
                      value={formik.values.prizePlacesCount}
                      onChange={handlePrizePlacesCountChange}
                      onKeyDown={handlePrizePlacesCountKeyDown}
                      onBlur={formik.handleBlur}
                      label="Prize Places Count"
                      type="text"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      fullWidth
                      error={Boolean(formik.touched.prizePlacesCount && formik.errors.prizePlacesCount)}
                      helperText={formik.touched.prizePlacesCount && formik.errors.prizePlacesCount}
                    />

                    <div className={css.prizes}>
                      {formik.values.prizes.length > 0 && <p className={css.prizesTitle}>Prizes</p>}

                      <FieldArray name="prizes">
                        {({ push, remove, form }) => (
                          <div className={css.prizesContent}>
                            {form.values.prizes.map((prize: LotteryPrizeForm, i: number) => {
                              const namePath = `prizes.${i}.name`;
                              const descriptionPath = `prizes.${i}.description`;
                              const nameError = getIn(form.errors, namePath);
                              const descriptionError = getIn(form.errors, descriptionPath);
                              const nameTouched = getIn(form.touched, namePath);
                              const descriptionTouched = getIn(form.touched, descriptionPath);

                              return (
                                <div key={i} className={css.prizeRow}>
                                  <TextField
                                    name={namePath}
                                    value={prize.name}
                                    onChange={form.handleChange}
                                    onBlur={form.handleBlur}
                                    label="Prize Name"
                                    size="small"
                                    fullWidth
                                    required
                                    error={Boolean(nameTouched && nameError)}
                                    helperText={nameTouched && nameError ? nameError : undefined}
                                  />

                                  <TextField
                                    name={descriptionPath}
                                    value={prize.description}
                                    onChange={form.handleChange}
                                    onBlur={form.handleBlur}
                                    label="Prize Description"
                                    size="small"
                                    fullWidth
                                    required
                                    error={Boolean(descriptionTouched && descriptionError)}
                                    helperText={descriptionTouched && descriptionError ? descriptionError : undefined}
                                  />

                                  <IconButton color="error" onClick={() => remove(i)} aria-label="Remove prize">
                                    <X width={18} height={18} />
                                  </IconButton>
                                </div>
                              );
                            })}

                            {form.values.prizes.length > 0 && <FormHelperText>{PRIZE_TEXT_HELPER}</FormHelperText>}

                            <Button variant="outlined" onClick={() => push({ ...LOTTERY_PRIZE_STUB })} fullWidth>
                              <Plus width={18} height={18} /> Prize
                            </Button>
                          </div>
                        )}
                      </FieldArray>
                    </div>

                    <Switch
                      name="isActive"
                      checked={formik.values.isActive}
                      onChange={formik.handleChange}
                      label="Active"
                    />
                  </form>
                </FormikProvider>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} color="error" variant="outlined">
                  Cancel
                </Button>

                <Button form="lotterySettingsForm" loading={isMutating} type="submit" variant="contained">
                  Update
                </Button>
              </DialogActions>
            </Fragment>
          )
        )}
      </Dialog>
    </Fragment>
  );
};
